import { prefersReducedMotion } from '@theme/utilities';
import { loadGoogleMaps } from '@theme/google-maps';

const EARTH_RADIUS_KM = 6371;
const ACTIVE_STORE_STORAGE_KEY_PREFIX = 'store-locator-active-handle';
const MAX_NEARBY_RESULTS = 10;

/**
 * @typedef {object} Store
 * @property {string} handle - The store metaobject's handle.
 * @property {string} name - The store's display name.
 * @property {number | null} lat - Latitude, or null if the store has no geo value set.
 * @property {number | null} lng - Longitude, or null if the store has no geo value set.
 */

/**
 * Great-circle distance between two lat/lng points, in kilometres.
 * @param {{ lat: number, lng: number }} a - The first point.
 * @param {{ lat: number, lng: number }} b - The second point.
 * @returns {number} The distance between the two points, in kilometres.
 */
function haversineDistanceKm(a, b) {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);

  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(h));
}

if (!customElements.get('store-locator')) {
  customElements.define(
    'store-locator',
    // Extends HTMLElement directly rather than Component: this element manages its own DOM
    // (list reordering, tab switching) imperatively rather than through declarative refs, and
    // doesn't rely on Component's Section Rendering API morph integration.
    class StoreLocator extends HTMLElement {
      /** @type {Store[]} */
      #stores = [];
      /** @type {any} */
      #map;
      /** @type {Map<string, any>} */
      #markers = new Map();
      /** @type {Promise<void> | undefined} */
      #mapsReady;
      /** @type {number | undefined} */
      #searchDebounce;
      /** @type {(() => void) | undefined} */
      #geolocateClickHandler;
      /** @type {((event: Event) => void) | undefined} */
      #searchInputHandler;
      /** @type {(() => void) | undefined} */
      #azClickHandler;
      /** @type {Map<HTMLElement, () => void>} */
      #tabClickHandlers = new Map();
      /** @type {Map<HTMLElement, () => void>} */
      #cardClickHandlers = new Map();
      /** @type {((event: KeyboardEvent) => void) | undefined} */
      #tabKeydownHandler;
      /** @type {string} */
      #defaultLocationLabel = '';
      /** @type {{ lat: number, lng: number }} The current market's default point (e.g. Sydney for AU, Auckland for NZ). */
      #defaultLocation = { lat: -33.8688, lng: 151.2093 };
      /** @type {{ lat: number, lng: number }} The current market's initial wide-zoom map center. */
      #mapCenter = { lat: -25.2744, lng: 133.7751 };
      /** @type {number} The initial map zoom level for `#mapCenter`. */
      #mapCenterZoom = 4;
      /** @type {string} The Places Autocomplete region restriction (e.g. `"au"`, `"nz"`) for the current market. */
      #regionCode = 'au';
      /**
       * @type {string | undefined} Mirrors the `place` argument last passed to `#goToLocation`;
       * `undefined` when the current view has no location label (e.g. GPS-only or alphabetical browsing).
       */
      #currentPlaceLabel;
      /** @type {string} */
      #markerIconUrl = '';
      /** @type {any} Places autocomplete session token; groups keystrokes + the final selection into one billed session. */
      #autocompleteSessionToken;
      /** @type {{ prediction: any, label: string }[]} */
      #suggestions = [];
      /** @type {number} Index of the keyboard-highlighted suggestion, or -1 when none is active. */
      #activeSuggestionIndex = -1;
      /** @type {number} Monotonic id of the latest suggestions request; guards against out-of-order responses. */
      #suggestionsRequestId = 0;
      /** @type {((event: KeyboardEvent) => void) | undefined} */
      #searchKeydownHandler;
      /** @type {(() => void) | undefined} */
      #searchBlurHandler;
      /** @type {((event: MouseEvent) => void) | undefined} */
      #suggestionsClickHandler;
      /** @type {((event: MouseEvent) => void) | undefined} */
      #suggestionsMousedownHandler;

      /** Wires up all event listeners, renders the default location, and loads Google Maps if an API key is configured. */
      connectedCallback() {
        this.#stores = this.#readStoreData();
        this.#defaultLocationLabel = this.getAttribute('data-default-location-label') ?? '';
        this.#markerIconUrl = this.getAttribute('marker-icon-url') ?? '';
        this.#regionCode = this.getAttribute('data-region-code') ?? 'au';

        // Number.parseFloat (not Number) is required here: getAttribute returns null for a
        // missing attribute, and Number(null) is 0 — not NaN — which would silently defeat this
        // fallback guard. Number.parseFloat(null) correctly yields NaN.
        const defaultLat = Number.parseFloat(this.getAttribute('data-default-lat'));
        const defaultLng = Number.parseFloat(this.getAttribute('data-default-lng'));
        if (!Number.isNaN(defaultLat) && !Number.isNaN(defaultLng)) {
          this.#defaultLocation = { lat: defaultLat, lng: defaultLng };
        }

        const mapCenterLat = Number.parseFloat(this.getAttribute('data-map-center-lat'));
        const mapCenterLng = Number.parseFloat(this.getAttribute('data-map-center-lng'));
        const mapCenterZoom = Number.parseFloat(this.getAttribute('data-map-center-zoom'));
        if (!Number.isNaN(mapCenterLat) && !Number.isNaN(mapCenterLng)) {
          this.#mapCenter = { lat: mapCenterLat, lng: mapCenterLng };
        }
        if (!Number.isNaN(mapCenterZoom)) this.#mapCenterZoom = mapCenterZoom;

        this.#geolocateClickHandler = () => this.#useCurrentLocation();
        this.querySelector('[data-store-locator-geolocate]')?.addEventListener('click', this.#geolocateClickHandler);

        const searchInput = this.querySelector('[data-store-locator-search-input]');
        this.#searchInputHandler = (event) => this.#onSearchInput(event);
        this.#searchKeydownHandler = (event) => this.#onSearchKeydown(event);
        this.#searchBlurHandler = () => this.#clearSuggestions();
        searchInput?.addEventListener('input', this.#searchInputHandler);
        searchInput?.addEventListener('keydown', this.#searchKeydownHandler);
        searchInput?.addEventListener('blur', this.#searchBlurHandler);

        const suggestionsList = this.querySelector('[data-store-locator-suggestions]');
        this.#suggestionsClickHandler = (event) => this.#onSuggestionsClick(event);
        // Keep focus on the input when a suggestion is clicked, so `blur` doesn't clear the list first.
        this.#suggestionsMousedownHandler = (event) => event.preventDefault();
        suggestionsList?.addEventListener('mousedown', this.#suggestionsMousedownHandler);
        suggestionsList?.addEventListener('click', this.#suggestionsClickHandler);

        this.#azClickHandler = () => this.#sortAlphabetically();
        this.querySelector('[data-store-locator-az]')?.addEventListener('click', this.#azClickHandler);

        this.#tabKeydownHandler = (event) => this.#onTabKeydown(event);
        for (const tab of this.querySelectorAll('[data-store-locator-tab]')) {
          const handler = () => this.#setActiveTab(tab.dataset.storeLocatorTab);
          this.#tabClickHandlers.set(tab, handler);
          tab.addEventListener('click', handler);
          tab.addEventListener('keydown', this.#tabKeydownHandler);
        }
        this.#setActiveTab('store');
        this.#goToLocation(this.#defaultLocation, this.#defaultLocationLabel);

        for (const card of this.querySelectorAll('[data-store-card]')) {
          if (!(card instanceof HTMLAnchorElement)) continue;
          const handler = () => {
            try {
              sessionStorage.setItem(this.#activeStoreStorageKey(), card.dataset.storeHandle ?? '');
            } catch (error) {
              console.warn('Store locator: could not persist the active store.', error);
            }
          };
          this.#cardClickHandlers.set(card, handler);
          card.addEventListener('click', handler);
        }

        try {
          const activeHandle = sessionStorage.getItem(this.#activeStoreStorageKey());
          if (activeHandle) {
            sessionStorage.removeItem(this.#activeStoreStorageKey());
            this.#setActiveStore(activeHandle);
          }
        } catch (error) {
          console.warn('Store locator: could not restore the active store.', error);
        }

        const apiKey = this.getAttribute('maps-api-key');
        if (!apiKey) {
          this.setAttribute('data-maps-unavailable', '');
          // Capped-out cards can normally only be revealed by clicking their map marker; with no map,
          // that reveal path doesn't exist, so nothing must stay hidden.
          this.#revealCappedStores();
          return;
        }

        this.#mapsReady = loadGoogleMaps(apiKey, { libraries: ['places'] })
          .then(() => this.#initMap())
          .catch((error) => {
            console.warn('Store locator: failed to load Google Maps.', error);
            this.setAttribute('data-maps-unavailable', '');
            this.#revealCappedStores();
          });
      }

      /** Tears down all event listeners registered in connectedCallback. */
      disconnectedCallback() {
        clearTimeout(this.#searchDebounce);

        this.querySelector('[data-store-locator-geolocate]')?.removeEventListener('click', this.#geolocateClickHandler);
        this.querySelector('[data-store-locator-az]')?.removeEventListener('click', this.#azClickHandler);

        const searchInput = this.querySelector('[data-store-locator-search-input]');
        searchInput?.removeEventListener('input', this.#searchInputHandler);
        searchInput?.removeEventListener('keydown', this.#searchKeydownHandler);
        searchInput?.removeEventListener('blur', this.#searchBlurHandler);

        const suggestionsList = this.querySelector('[data-store-locator-suggestions]');
        suggestionsList?.removeEventListener('mousedown', this.#suggestionsMousedownHandler);
        suggestionsList?.removeEventListener('click', this.#suggestionsClickHandler);

        for (const [tab, handler] of this.#tabClickHandlers) {
          tab.removeEventListener('click', handler);
          tab.removeEventListener('keydown', this.#tabKeydownHandler);
        }
        this.#tabClickHandlers.clear();

        for (const [card, handler] of this.#cardClickHandlers) {
          card.removeEventListener('click', handler);
        }
        this.#cardClickHandlers.clear();
      }

      /**
       * Reads the store list embedded by Liquid as a JSON script tag.
       * @returns {Store[]} The parsed store list, or an empty array if it's missing/invalid.
       */
      #readStoreData() {
        const script = this.querySelector('[data-store-locator-data]');
        if (!script) return [];
        try {
          return JSON.parse(script.textContent);
        } catch (error) {
          console.warn('Store locator: could not parse store data.', error);
          return [];
        }
      }

      /** Replaces the map placeholder with a real Google Map and drops a marker for each store. */
      #initMap() {
        const mapContainer = this.querySelector('[data-store-locator-map]');
        if (!mapContainer) return;

        mapContainer.replaceChildren();
        this.#map = new google.maps.Map(mapContainer, {
          center: this.#mapCenter,
          zoom: this.#mapCenterZoom,
        });

        for (const store of this.#stores) {
          if (typeof store.lat !== 'number' || typeof store.lng !== 'number') continue;

          const marker = new google.maps.Marker({
            map: this.#map,
            position: { lat: store.lat, lng: store.lng },
            title: store.name,
            icon: this.#markerIconUrl
              ? {
                  url: this.#markerIconUrl,
                  scaledSize: new google.maps.Size(32, 32),
                  anchor: new google.maps.Point(16, 32),
                }
              : undefined,
          });
          marker.addListener('click', () => this.#focusStoreCard(store.handle));
          this.#markers.set(store.handle, marker);
        }
      }

      /**
       * Pans and zooms the map to the given store's marker.
       * @param {string} handle - The store's metaobject handle.
       */
      #panToStore(handle) {
        const marker = this.#markers.get(handle);
        if (!marker || !this.#map) return;
        this.#map.panTo(marker.getPosition());
        this.#map.setZoom(Math.max(this.#map.getZoom(), 14));
      }

      /**
       * Scrolls a store's card into view, focuses it, and pans the map to its marker, without navigating away.
       * @param {string} handle - The store's metaobject handle.
       */
      #focusStoreCard(handle) {
        this.#setActiveTab('store');
        this.#setActiveStore(handle);

        const card = this.querySelector(`[data-store-card][data-store-handle="${CSS.escape(handle)}"]`);
        // A marker outside the closest MAX_NEARBY_RESULTS can still be clicked; reveal its card too.
        const item = card?.closest('.store-locator__item');
        if (item instanceof HTMLElement && item.hidden) {
          item.hidden = false;
          this.#updateCount(this.#visibleCount(), this.#currentPlaceLabel);
        }
        card?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
        if (card instanceof HTMLAnchorElement) card.focus();
        this.#panToStore(handle);
      }

      /**
       * Marks the given store's card as active, clearing the active state from any other card.
       * @param {string} handle - The store's metaobject handle.
       */
      #setActiveStore(handle) {
        for (const card of this.querySelectorAll('.store-card--active')) {
          card.classList.remove('store-card--active');
          card.removeAttribute('aria-current');
        }

        const card = this.querySelector(`[data-store-card][data-store-handle="${CSS.escape(handle)}"]`);
        card?.classList.add('store-card--active');
        card?.setAttribute('aria-current', 'true');
      }

      /** @returns {string} The sessionStorage key used to persist the active store, namespaced per section instance. */
      #activeStoreStorageKey() {
        return `${ACTIVE_STORE_STORAGE_KEY_PREFIX}-${this.getAttribute('section-id')}`;
      }

      /**
       * Requests the shopper's location via the Geolocation API, falling back to the default
       * location if it's unavailable or denied. On any failure, also shows a tooltip explaining
       * that location access/services are required, so the fallback to the default location
       * doesn't look like a silent no-op.
       */
      #useCurrentLocation() {
        if (!navigator.geolocation) {
          this.#showLocationAccessNotice();
          this.#goToLocation(this.#defaultLocation, this.#defaultLocationLabel);
          return;
        }

        navigator.geolocation.getCurrentPosition(
          (position) => {
            const origin = { lat: position.coords.latitude, lng: position.coords.longitude };
            this.#goToLocation(origin);
          },
          () => {
            this.#showLocationAccessNotice();
            this.#goToLocation(this.#defaultLocation, this.#defaultLocationLabel);
          },
          // Without an explicit timeout, PositionOptions.timeout defaults to Infinity: when the
          // device's location services are off (rather than just the site's permission being
          // denied), some browsers never invoke either callback, so the error handling above
          // (and its tooltip) would otherwise never run. A short value keeps that wait bounded
          // without slowing down an outright PERMISSION_DENIED, which browsers report as soon as
          // they resolve the permission check rather than waiting out this timeout.
          { timeout: 3000 }
        );
      }

      /** Shows the "location access needed" tooltip anchored to the geolocate button. */
      #showLocationAccessNotice() {
        const popover = this.querySelector('[data-store-locator-location-notice] [popover]');
        // showPopover() throws if already open — a fast repeat failure (e.g. permission already
        // denied from a prior visit) can trigger overlapping calls before the first popover closes.
        if (popover instanceof HTMLElement && !popover.matches(':popover-open')) popover.showPopover();
      }

      /**
       * Sorts the list by distance from the given point and recentres the map. Falls back to `place`
       * (e.g. "Sydney, Australia") when no more specific place name is known.
       * @param {{ lat: number, lng: number }} origin - The point to sort/centre from.
       * @param {string} [place] - A human-readable label for the origin, if known.
       */
      #goToLocation(origin, place) {
        this.#currentPlaceLabel = place;
        this.#sortByDistance(origin);
        this.#updateCount(this.#visibleCount(), place);
        if (this.#map) {
          this.#map.panTo(origin);
          this.#map.setZoom(11);
        }
      }

      /**
       * Debounces search input and requests Places autocomplete suggestions once the shopper pauses.
       * @param {Event} event - The search input's `input` event.
       */
      #onSearchInput(event) {
        const query = /** @type {HTMLInputElement} */ (event.target).value.trim();
        clearTimeout(this.#searchDebounce);
        if (!query) {
          this.#clearSuggestions();
          return;
        }

        this.#searchDebounce = setTimeout(() => this.#requestSuggestions(query), 250);
      }

      /**
       * Fetches AU suburb/postcode autocomplete predictions for the query and renders them.
       * Degrades to a no-op (plain input) if the Places library isn't available.
       * @param {string} query - The shopper-entered search text.
       * @returns {Promise<void>}
       */
      async #requestSuggestions(query) {
        await this.#mapsReady;
        const AutocompleteSuggestion = window.google?.maps?.places?.AutocompleteSuggestion;
        if (!AutocompleteSuggestion) return;

        this.#autocompleteSessionToken ??= new google.maps.places.AutocompleteSessionToken();
        const requestId = ++this.#suggestionsRequestId;

        try {
          const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input: query,
            sessionToken: this.#autocompleteSessionToken,
            includedRegionCodes: [this.#regionCode],
            includedPrimaryTypes: ['locality', 'postal_code'],
          });

          // Ignore responses that arrive out of order behind a newer request.
          if (requestId !== this.#suggestionsRequestId) return;

          this.#suggestions = (suggestions ?? [])
            .map((suggestion) => suggestion.placePrediction)
            .filter(Boolean)
            .map((prediction) => ({ prediction, label: prediction.text?.text ?? '' }));

          if (!this.#suggestions.length) {
            this.#clearSuggestions();
            this.#announceSuggestions(this.getAttribute('data-suggestions-none'));
            return;
          }
          this.#renderSuggestions();
        } catch (error) {
          if (requestId !== this.#suggestionsRequestId) return;
          console.warn('Store locator: autocomplete request failed.', error);
          this.#clearSuggestions();
          this.#announceSuggestions(this.getAttribute('data-search-unavailable'));
        }
      }

      /** Renders the current suggestions into the listbox and opens it. */
      #renderSuggestions() {
        const listbox = this.querySelector('[data-store-locator-suggestions]');
        const input = this.querySelector('[data-store-locator-search-input]');
        if (!listbox || !input) return;

        if (!this.#suggestions.length) {
          this.#clearSuggestions();
          return;
        }

        listbox.replaceChildren(
          ...this.#suggestions.map(({ label }, index) => {
            const option = document.createElement('li');
            option.id = `${listbox.id}-option-${index}`;
            option.className = 'store-locator__suggestion';
            option.setAttribute('role', 'option');
            option.setAttribute('aria-selected', 'false');
            option.textContent = label;
            return option;
          })
        );

        listbox.hidden = false;
        input.setAttribute('aria-expanded', 'true');
        this.#activeSuggestionIndex = -1;
        input.removeAttribute('aria-activedescendant');

        const template = this.getAttribute('data-suggestions-available-template') ?? '';
        this.#announceSuggestions(template.replace('__COUNT__', String(this.#suggestions.length)));
      }

      /** Closes the suggestion listbox and resets its state. */
      #clearSuggestions() {
        clearTimeout(this.#searchDebounce);

        const listbox = this.querySelector('[data-store-locator-suggestions]');
        const input = this.querySelector('[data-store-locator-search-input]');

        this.#suggestions = [];
        this.#activeSuggestionIndex = -1;
        if (listbox) {
          listbox.replaceChildren();
          listbox.hidden = true;
        }
        if (input) {
          input.setAttribute('aria-expanded', 'false');
          input.removeAttribute('aria-activedescendant');
        }
      }

      /**
       * Announces suggestion availability to assistive tech via the visually-hidden live region.
       * @param {string | null} message - The message to announce.
       */
      #announceSuggestions(message) {
        const status = this.querySelector('[data-store-locator-suggestions-status]');
        if (status) status.textContent = message ?? '';
      }

      /**
       * Handles arrow/enter/escape keys while the suggestion listbox is open.
       * @param {KeyboardEvent} event - The search input's `keydown` event.
       */
      #onSearchKeydown(event) {
        if (!this.#suggestions.length) return;

        switch (event.key) {
          case 'ArrowDown':
            event.preventDefault();
            this.#moveActiveSuggestion(1);
            break;
          case 'ArrowUp':
            event.preventDefault();
            this.#moveActiveSuggestion(-1);
            break;
          case 'Enter':
            // With nothing highlighted, Enter picks the top suggestion.
            event.preventDefault();
            this.#selectSuggestion(this.#activeSuggestionIndex >= 0 ? this.#activeSuggestionIndex : 0);
            break;
          case 'Escape':
            // Prevent the browser's native search-input clear; just close the list, keep the text.
            event.preventDefault();
            this.#clearSuggestions();
            break;
          default:
            break;
        }
      }

      /**
       * Moves the keyboard highlight through the suggestions, wrapping at both ends.
       * @param {number} delta - `1` for the next suggestion, `-1` for the previous.
       */
      #moveActiveSuggestion(delta) {
        const count = this.#suggestions.length;
        // From no selection, ArrowDown lands on the first item and ArrowUp on the last.
        const index =
          this.#activeSuggestionIndex === -1
            ? delta > 0
              ? 0
              : count - 1
            : (this.#activeSuggestionIndex + delta + count) % count;

        const listbox = this.querySelector('[data-store-locator-suggestions]');
        const input = this.querySelector('[data-store-locator-search-input]');
        const options = listbox?.querySelectorAll('[role="option"]') ?? [];

        options.forEach((option, i) => {
          const isActive = i === index;
          option.classList.toggle('store-locator__suggestion--active', isActive);
          option.setAttribute('aria-selected', String(isActive));
        });

        this.#activeSuggestionIndex = index;
        input?.setAttribute('aria-activedescendant', options[index]?.id ?? '');
        options[index]?.scrollIntoView({ block: 'nearest' });
      }

      /**
       * Handles a click on a suggestion in the listbox.
       * @param {MouseEvent} event - The listbox's `click` event.
       */
      #onSuggestionsClick(event) {
        const option = /** @type {HTMLElement} */ (event.target).closest('[role="option"]');
        const listbox = this.querySelector('[data-store-locator-suggestions]');
        if (!option || !listbox) return;

        const index = [...listbox.children].indexOf(option);
        if (index >= 0) this.#selectSuggestion(index);
      }

      /**
       * Resolves the selected suggestion to coordinates and re-centres the list/map on it.
       * @param {number} index - The index of the suggestion to select.
       * @returns {Promise<void>}
       */
      async #selectSuggestion(index) {
        const entry = this.#suggestions[index];
        if (!entry) return;

        const input = this.querySelector('[data-store-locator-search-input]');
        if (input instanceof HTMLInputElement) input.value = entry.label;
        this.#clearSuggestions();

        try {
          const place = entry.prediction.toPlace();
          await place.fetchFields({ fields: ['location', 'formattedAddress'] });
          if (!place.location) return;
          this.#goToLocation(
            { lat: place.location.lat(), lng: place.location.lng() },
            place.formattedAddress ?? entry.label
          );
        } catch (error) {
          console.warn('Store locator: could not resolve the selected place.', error);
        } finally {
          // Selecting a place ends the billing session; the next search starts a fresh one.
          this.#autocompleteSessionToken = undefined;
        }
      }

      /**
       * Reorders the store list by distance, updates each card's distance label, and hides all but
       * the closest `MAX_NEARBY_RESULTS` stores — unless Maps is unavailable, in which case nothing
       * is hidden, since the marker-click reveal path for capped-out cards wouldn't exist.
       * @param {{ lat: number, lng: number }} origin - The point to measure distance from.
       */
      #sortByDistance(origin) {
        const list = this.querySelector('[data-store-locator-list]');
        if (!list) return;

        const canCapResults = !this.hasAttribute('data-maps-unavailable');
        const distanceTemplate = this.getAttribute('data-distance-template') ?? '__DISTANCE__ km away';
        const items = [...list.children];

        const withDistance = items.map((item) => {
          const handle = /** @type {HTMLElement | undefined} */ (item.querySelector('[data-store-card]'))?.dataset
            .storeHandle;
          const store = this.#stores.find((candidate) => candidate.handle === handle);
          const distanceKm =
            store && typeof store.lat === 'number' && typeof store.lng === 'number'
              ? haversineDistanceKm(origin, { lat: store.lat, lng: store.lng })
              : Number.POSITIVE_INFINITY;
          return { item, distanceKm };
        });

        withDistance.sort((a, b) => a.distanceKm - b.distanceKm);

        withDistance.forEach(({ item, distanceKm }, index) => {
          const distanceEl = item.querySelector('[data-store-distance]');
          if (distanceEl && Number.isFinite(distanceKm)) {
            const textEl = distanceEl.querySelector('[data-store-distance-text]');
            if (textEl) textEl.textContent = distanceTemplate.replace('__DISTANCE__', distanceKm.toFixed(1));
            distanceEl.hidden = false;
          }
          item.hidden = canCapResults && index >= MAX_NEARBY_RESULTS;
          list.appendChild(item);
        });
      }

      /** Un-hides every store card. Used when Maps becomes unavailable after a cap was already applied. */
      #revealCappedStores() {
        const list = this.querySelector('[data-store-locator-list]');
        if (!list) return;

        for (const item of /** @type {HTMLElement[]} */ ([...list.children])) item.hidden = false;
        this.#updateCount(this.#visibleCount(), this.#currentPlaceLabel);
      }

      /**
       * Reorders the full store list alphabetically by name, revealing every card regardless of the
       * distance cap, and clears any distance labels. "View A-Z" is a distinct "browse the whole
       * catalogue" action, not a re-sort of the capped nearby-stores view.
       */
      #sortAlphabetically() {
        const list = this.querySelector('[data-store-locator-list]');
        if (!list) return;

        const items = /** @type {HTMLElement[]} */ ([...list.children]);
        const withName = items.map((item) => {
          const handle = /** @type {HTMLElement | undefined} */ (item.querySelector('[data-store-card]'))?.dataset
            .storeHandle;
          const store = this.#stores.find((candidate) => candidate.handle === handle);
          return { item, name: store?.name ?? '' };
        });

        withName.sort((a, b) => a.name.localeCompare(b.name));

        this.#currentPlaceLabel = undefined;
        for (const { item } of withName) {
          const distanceEl = item.querySelector('[data-store-distance]');
          if (distanceEl) {
            distanceEl.hidden = true;
            const textEl = distanceEl.querySelector('[data-store-distance-text]');
            if (textEl) textEl.textContent = '';
          }
          item.hidden = false;
          list.appendChild(item);
        }

        this.#updateCount(this.#visibleCount());
      }

      /** @returns {number} The number of store cards currently visible in the list. */
      #visibleCount() {
        const list = this.querySelector('[data-store-locator-list]');
        if (!list) return 0;
        return [...list.children].filter((item) => !item.hidden).length;
      }

      /**
       * Picks the correctly-pluralized template for the given count and fills in the real count/place.
       * @param {number} count - The number of stores currently visible.
       * @param {string} [place] - A human-readable place label, if the count is location-scoped.
       */
      #updateCount(count, place) {
        const countEl = this.querySelector('[data-store-locator-count]');
        if (!countEl) return;

        const isSingular = count === 1;
        const attribute = place
          ? isSingular
            ? 'data-count-template-near-singular'
            : 'data-count-template-near-plural'
          : isSingular
            ? 'data-count-template-singular'
            : 'data-count-template-plural';

        let template = this.getAttribute(attribute);
        if (!template) return;

        // The plural template is rendered with a literal placeholder count (2) so Shopify's `t`
        // filter resolves the "other" CLDR category — `count:` can't take a non-numeric
        // placeholder and still pluralize correctly. Swap that placeholder for the real count.
        if (!isSingular) template = template.replace(/^\d+/, String(count));
        if (place) template = template.replace('__PLACE__', place);

        countEl.textContent = template;
      }

      /**
       * Switches the mobile Store/Map tab. Has no visual effect above the mobile breakpoint.
       * @param {string} tab - Either `"store"` or `"map"`.
       */
      #setActiveTab(tab) {
        this.setAttribute('data-active-tab', tab);
        const tabs = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('[data-store-locator-tab]'));
        for (const button of tabs) {
          const isActive = button.dataset.storeLocatorTab === tab;
          button.setAttribute('aria-selected', String(isActive));
          button.setAttribute('tabindex', isActive ? '0' : '-1');
        }
      }

      /**
       * Moves focus between tabs on Arrow/Home/End, per the WAI-ARIA APG tabs pattern.
       * @param {KeyboardEvent} event - The keydown event.
       */
      #onTabKeydown(event) {
        const tabs = [...this.querySelectorAll('[data-store-locator-tab]')];
        const currentIndex = tabs.indexOf(/** @type {HTMLElement} */ (event.currentTarget));
        if (currentIndex === -1) return;

        let nextIndex;
        switch (event.key) {
          case 'ArrowRight':
          case 'ArrowDown':
            nextIndex = (currentIndex + 1) % tabs.length;
            break;
          case 'ArrowLeft':
          case 'ArrowUp':
            nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
            break;
          case 'Home':
            nextIndex = 0;
            break;
          case 'End':
            nextIndex = tabs.length - 1;
            break;
          default:
            return;
        }

        event.preventDefault();
        const nextTab = /** @type {HTMLElement} */ (tabs[nextIndex]);
        nextTab.focus();
        this.#setActiveTab(nextTab.dataset.storeLocatorTab);
      }
    }
  );
}
